import { Action } from '@reduxjs/toolkit';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useAccount } from '~/apis';
import { useAuthState } from '~/auth';

import { getAuthToken } from './authStorage';
import { config } from './config';
import logger from './logger';

/**
 * Custom hook that waits for authentication to be ready.
 * Returns true when auth is settled and, for online modes, token is available in authStorage.
 */
export const useAuthReady = (): boolean => {
  const { isAuthorized, isAuthSettled } = useAuthState();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const token = getAuthToken();
    const hasToken = !!token || config.useMockedAuthToken;

    logger.debug(
      {
        isAuthorized,
        isAuthSettled,
        hasToken,
        useMockedAuth: config.useMockedAuthToken,
        token: token ? 'present' : 'missing',
      },
      'useAuthReady: Checking auth state',
    );

    setIsReady(isAuthSettled && (isAuthorized ? hasToken : true));
  }, [isAuthorized, isAuthSettled]);

  return isReady;
};

type AuthorizedDispatchParam = Action | null | ((dispatch: ReturnType<typeof useDispatch>) => void);

/**
 * Hook that dispatches an action or calls a callback when authentication and account data are ready
 * Only dispatches once when both auth and account become ready, not on param changes
 */
export const useAuthorizedDispatchOnMount = (param: AuthorizedDispatchParam) => {
  const dispatch = useDispatch();
  const { isAuthorized, isAuthSettled } = useAuthState();
  const { isLoading: isAccountLoading, data: account } = useAccount();
  const hasDispatched = useRef(false);
  const paramRef = useRef<AuthorizedDispatchParam>(param);

  paramRef.current = param;

  const token = getAuthToken();
  const hasToken = !!token || config.useMockedAuthToken;

  // Online: wait for auth settled, token present, and account resolved.
  // Offline/unauthorized: account fetch is irrelevant and may remain loading in some states;
  // only require auth settled with no token.
  const isReady = isAuthSettled ? (isAuthorized ? !isAccountLoading : !hasToken) : false;

  useEffect(() => {
    const currentParam = paramRef.current;

    if (isReady && currentParam && !hasDispatched.current) {
      hasDispatched.current = true;
      if (typeof currentParam === 'function') {
        logger.debug(
          {
            isAuthorized,
            hasToken,
            isAccountLoading,
            hasAccount: !!account,
          },
          'useAuthorizedDispatchOnMount: Ready, calling callback',
        );
        currentParam(dispatch);
      } else {
        logger.debug(
          {
            actionType: currentParam.type,
            isAuthorized,
            hasToken,
            isAccountLoading,
            hasAccount: !!account,
          },
          'useAuthorizedDispatchOnMount: Ready, dispatching action',
        );
        dispatch(currentParam);
      }
    } else if (!isReady && currentParam) {
      logger.debug(
        {
          isAuthorized,
          hasToken,
          isAccountLoading,
          hasAccount: !!account,
        },
        'useAuthorizedDispatchOnMount: Not ready, waiting to dispatch/call callback',
      );
    }
  }, [isReady, dispatch, isAuthorized, hasToken, isAccountLoading, account]);

  return { isAuthReady: isReady };
};
